import { z } from "zod";
import { VERIFICATION_STATUS } from "@goppy/domain";
import { paymentConfirmationStatusSchema } from "./collections.js";
import { evidenceContentKindSchema } from "./contribution-evidence.js";
import { groupRoleSchema } from "./groups.js";

/* ===========================================================================
 * US-E24.F9-0358-S01 (@UC9588) — ĐỌC LẠI LỜI KHAI ĐÃ NỘP.
 * GET /v1/member-dues/:id/confirmations — chủ due đọc của mình; O/A/T đọc của người khác.
 *
 * 🔴 §3.7 BA TRẠNG THÁI TIỀN KHÔNG GỘP: endpoint này trả **trạng thái 2 (lời khai)**, KHÔNG phải
 *    "tiền đã vào quỹ". Contract cố ý KHÔNG có `ledgerTxId` / field số dư nào và `.strict()` ⇒
 *    "claim đã thành fact" là trạng thái **KHÔNG BIỂU DIỄN ĐƯỢC** (không thể lặng lẽ đổi nghĩa).
 * 🔴 KHÔNG whitelist thứ hai: `status` reuse `paymentConfirmationStatusSchema` (collections.ts),
 *    `verificationStatus` reuse `VERIFICATION_STATUS` (@goppy/domain = whitelist DB CHECK).
 * 🔴 PII: `proofUrl` là presigned GET **TTL NGẮN** resolve tại read-time (DB lưu KEY, không URL) —
 *    `proofUrlExpiresAt` cho client biết khi nào phải refetch. Ảnh sao kê ngân hàng là PII công dân
 *    VN: link sống càng ngắn càng tốt.
 * ========================================================================= */

/** Tiền = STRING minor units (B1) — không float, không number trên wire. */
const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/** Trạng thái soát ẢNH bằng chứng (khác `status` = quyết định nghiệp vụ — schema.prisma:1576-1581). */
export const proofVerificationStatusSchema = z.enum([
  VERIFICATION_STATUS.VALID,
  VERIFICATION_STATUS.INVALID,
  VERIFICATION_STATUS.UNVERIFIED,
]);

/**
 * Lời khai chuyển khoản MỚI NHẤT của 1 member_due (§7 mục 4 — mới nhất, không phải lịch sử).
 * Trả **bất kể status** (kể cả `rejected`) để màn "bằng chứng bị từ chối vì X — báo lại" dựng được
 * (§7 mục 2): giấu `rejectReason` thì người nộp lại y như cũ.
 */
export const paymentConfirmationReadSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    /** Số NGƯỜI KHAI đã chuyển (claim) — KHÔNG phải số quỹ đã nhận. STRING minor. */
    claimedAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    status: paymentConfirmationStatusSchema,
    submittedAt: z.string().datetime(),
    reviewedAt: z.string().datetime().nullable(),
    rejectReason: z.string().nullable(),
    verificationStatus: proofVerificationStatusSchema,
    /** Presigned GET TTL ngắn; null khi khai KHÔNG kèm ảnh. */
    proofUrl: z.string().nullable(),
    /** Hạn của `proofUrl`; null khi không có ảnh (hoặc URL công khai không hết hạn). */
    proofUrlExpiresAt: z.string().datetime().nullable(),
    /** 🔴 US-E6.F3-0910 (@UC9588) — `image` | `pdf` để client chọn cách hiển thị; null khi không có file. */
    proofContentKind: evidenceContentKindSchema.nullable(),
    /**
     * 🔴 US-AI-0496-S03 (@UC9803) — GHI CHÚ người khai tự viết; `null` khi họ bỏ trống.
     *
     * Cột `note` sinh ra để **thủ quỹ đối chiếu sao kê**, nên nó PHẢI có mặt ở đường ĐỌC này. Thêm
     * cột mà không trả ra là đúng thứ QĐ PO bác bỏ: *"hỏi rồi giấu ở chỗ thủ quỹ không mở"*.
     * 🔴 Là CHỮ do người dùng gõ ⇒ tầng vẽ phải escape như mọi chuỗi người dùng khác; nó KHÔNG
     * bao giờ được đọc như một con số hay tham gia phép tính.
     */
    note: z.string().nullable(),
  })
  .strict();
export type PaymentConfirmationRead = z.infer<
  typeof paymentConfirmationReadSchema
>;

/**
 * Envelope. Due tồn tại nhưng CHƯA TỪNG khai ⇒ `{confirmation: null}` với HTTP **200** — KHÔNG 404
 * (404 ở đây là nói dối rằng due không tồn tại).
 */
export const paymentConfirmationReadResponseSchema = z
  .object({
    confirmation: paymentConfirmationReadSchema.nullable(),
    /**
     * 🔴 US-NOTIF-0750 (@UC0051) — TÊN NGƯỜI KHAI, ở ENVELOPE chứ không trong `confirmation`.
     *
     * Đúng chỗ vì nó thuộc về **member_due**, không thuộc lời khai: due tồn tại mà chưa ai khai
     * (`confirmation: null`) thì tên người phải đóng vẫn có thật.
     *
     * Vì sao thêm: màn Duyệt lời khai của app lấy tên từ **DÒNG FEED** truyền qua `extra` — nên khi
     * mở từ THÔNG BÁO (deep-link không mang `extra`) màn không dựng được. Docblock của
     * `PaymentClaimScreen` đã ghi thẳng khoảng trống này: *"đường đọc lời khai không trả tên"*.
     *
     * `null` khi ghế chưa có tên lẫn người dùng gắn (ghế `unclaimed`) — KHÔNG bịa chuỗi rỗng. Chuỗi
     * fallback theo `displayName ?? primaryUser.fullName ?? null`, đúng thứ tự
     * `pending-dues.service.ts` đang dùng: một luật, một chỗ.
     */
    payerName: z.string().nullable(),
    /**
     * 🔴 VAI CỦA NGƯỜI ĐANG XEM trong nhóm — do SERVER nói, client không suy.
     *
     * Màn duyệt dựng hai nút GHI TIỀN theo vai này. Trước đây vai cũng đi qua `extra` của dòng
     * feed; deep-link không có `extra` ⇒ hoặc phải đoán (không được phép ở một màn ghi tiền), hoặc
     * phải gọi thêm một lượt hồ sơ nhóm. Guard của chính route này (`MemberDueReadAccessGuard`)
     * ĐÃ resolve vai để quyết định cho đọc hay không — trả nó ra là dùng lại phép đã tính, không
     * đẻ nguồn thứ hai.
     *
     * ⚠️ Đây là dữ liệu HIỂN THỊ. Chốt chặn thật vẫn ở `@RequireFundRoles(...PAYMENT_APPROVER_ROLES)`
     * trên hai route approve/decline — sửa giá trị này KHÔNG nới được quyền ghi.
     */
    viewerRole: groupRoleSchema,
  })
  .strict();
export type PaymentConfirmationReadResponse = z.infer<
  typeof paymentConfirmationReadResponseSchema
>;
